import type { ReactNode } from 'react';
import { useSearch } from '@tanstack/react-router';
import type { FilingObligationDto } from '@signalvote/shared';
import { alDailyObligationStripBody, alDailyWindowBannerBody } from '@signalvote/shared';
import { useObligations } from '@/hooks/queries/useFinanceDeadlines';
import { useAlDailyWindow } from '@/hooks/queries/useFinanceCompliance';
import { useCreateAttestation } from '@/hooks/queries/useCommitteeFormation';
import { formatAmount, formatDate } from '../utils';
import { timeRemainingLabel } from './noticeCountdown';
import { RapidAction, DailyAction, PreSessionAction, NoticeAction } from './queueActions';
import {
  dismissFiledElsewhereConfirmCopy,
  noticeFilingModel,
  type QueueConfirmCopy,
} from './actionQueueConfirmCopy';
import { usePortalUploadModel, filingDestinationPhrase } from '@/hooks/finance/usePortalUploadModel';
import { receivingOfficePhrase } from '../reports/receivingOffice';
import { answered } from '@/lib/reportsRead';

/**
 * Descriptor assembly for the ranked action queue — extracted from
 * ActionQueue.tsx so the queue shell (deck/expand presentation) stays separate
 * from what queues and which mutation each item carries.
 *
 * Membership rule (settled in the grill session): an item queues iff it has an
 * action. Fixed ranking:
 *   1. OVERDUE rapid reports — rose "FILE NOW" + Generate + "filed elsewhere"
 *      dismiss (the existing attestation).
 *   2. the owed daily report — Mark-filed-in-portal. The standing AL
 *      daily-window explainer sentence rides HERE as this item's detail (the
 *      old standalone blue strip is deleted).
 *   3. NON-OVERDUE rapid reports (imminent/upcoming) — amber "DUE SOON", same
 *      Generate / mark-reported / attestation-dismiss actions as the overdue
 *      ones. These used to live in RapidReportAlertStrip; the queue must show
 *      EVERY rapid obligation, not only the overdue slice.
 */

export interface QueueDescriptor {
  id: string;
  /** Rose (overdue rapid / daily) vs amber (non-overdue rapid) top
   *  border + label tint. */
  tone: 'rose' | 'amber';
  /** True for any rapid-report obligation (overdue OR not) — drives the
   *  `?action=rapid` auto-expand membership check. */
  isRapid: boolean;
  /** Small mono uppercase label on the front card. */
  frontLabel: string;
  title: string;
  detail: ReactNode;
  /** Renders the primary action; `resolve` drops the item and promotes the next.
   *  `inert` (collapsed-deck front card) strips the button from the tab order and
   *  hides it from AT so the whole stack is the single tab stop. */
  renderAction: (resolve: () => void, inert: boolean) => ReactNode;
  /** The dismiss × (omitted when the item can't be dismissed). It's a
   *  committing "filed elsewhere" attestation, so it ALWAYS confirms
   *  (ADR-023) — bundling the copy with the handler makes that structural:
   *  the × opens `confirm`; `onAccept` only fires on accept. */
  dismiss?: {
    label: string;
    confirm: QueueConfirmCopy;
    onAccept: (resolve: () => void) => void;
  };
}

export function useActionQueueDescriptors(): {
  descriptors: QueueDescriptor[];
  forceRapid: boolean;
  /** Why the obligations behind the queue could not be read, so the hub never shows an empty queue it can't vouch for. */
  unready: string | null;
} {
  const obligationsRead = useObligations();
  const portal = usePortalUploadModel();
  const search = useSearch({ strict: false }) as { action?: string };
  const forceRapid = search.action === 'rapid';
  const { mutate: attest } = useCreateAttestation();
  // The standing AL daily-window explainer (the old blue banner) folds into the
  // daily item's detail when the campaign is in the pre-election window.
  const alWindow = answered(useAlDailyWindow(undefined));
  const electionEve = alWindow?.windowEnd ?? alWindow?.electionDate ?? null;
  const windowExplainer = alWindow?.isInWindow
    ? alDailyWindowBannerBody(electionEve ? formatDate(electionEve) : 'the election')
    : null;

  const obligations = obligationsRead.status === 'ready' ? obligationsRead.data.obligations : [];
  const unready =
    obligationsRead.status === 'failed'
      ? "Couldn't load your filing obligations. Try again shortly."
      : obligationsRead.status === 'offline'
        ? "You're offline, so your filing obligations can't be checked."
        : null;
  // Every rapid obligation queues (regression fix): the old RapidReportAlertStrip
  // showed imminent/upcoming ones too. Split by overdue so ranking + tone differ.
  const rapids = obligations.filter((o) => o.isRapidReport);
  const overdueRapid = rapids.filter((o) => o.urgency === 'overdue');
  const upcomingRapid = rapids.filter((o) => o.urgency !== 'overdue');
  const daily = obligations.filter((o) => o.obligationKind === 'DailyAggregate');
  // VA §24.2-947.11 pre-session report (GitHub #164) — ranks like a rapid: an
  // overdue one rises to the overdue tier, an imminent/upcoming one sits in the
  // due-soon tier. Gated purely on obligationKind — never a stateCode==='VA'
  // string (VA COMET UI convention) — so AL/GA/LA decks never see it.
  const preSession = obligations.filter((o) => o.obligationKind === 'PreSessionAggregate');
  const overduePreSession = preSession.filter((o) => o.urgency === 'overdue');
  const upcomingPreSession = preSession.filter((o) => o.urgency !== 'overdue');
  // 48-hour notices — NC §163-278.9(a)(2) (GitHub #301) and the federal 11 CFR 104.5(f) Form 6
  // notice (#708). They rank like a rapid by urgency; their card (NoticeAction) is one- or two-state
  // depending on whether the app can build the artifact, which the SERVER decides via `canGenerate`.
  // Gated purely on obligationKind, so a deck in a jurisdiction with no notice regime never sees one.
  const notices = obligations.filter((o) => o.obligationKind === 'RapidNotice');
  const overdueNotice = notices.filter((o) => o.urgency === 'overdue');
  const upcomingNotice = notices.filter((o) => o.urgency !== 'overdue');

  // A rapid item's descriptor — identical actions/dismiss regardless of urgency;
  // only tone + front label + the "past due" detail line differ.
  const rapidDescriptor = (o: FilingObligationDto, overdue: boolean): QueueDescriptor => {
    const canDismiss = o.periodStart !== null && o.periodEnd !== null;
    return {
      id: o.id,
      isRapid: true,
      tone: overdue ? 'rose' : 'amber',
      frontLabel: overdue ? 'File now' : 'Due soon',
      title: o.name,
      detail: (
        <>
          Due {formatDate(o.dueDate)}
          {o.daysRemaining < 0 ? ` · ${Math.abs(o.daysRemaining)} days past due` : ''}
        </>
      ),
      renderAction: (resolve, inert) => (
        <RapidAction obligation={o} onResolved={resolve} inert={inert} />
      ),
      dismiss: canDismiss
        ? {
            label: `Dismiss ${o.name} — already filed elsewhere`,
            confirm: dismissFiledElsewhereConfirmCopy(o),
            onAccept: (resolve) => {
              // "Filed elsewhere" attestation — the same endpoint + shape the old
              // overdue-row dismiss fired. Retires this obligation, then promotes.
              attest({
                scheduleName: o.name,
                periodStart: o.periodStart!,
                periodEnd: o.periodEnd!,
              });
              resolve();
            },
          }
        : undefined,
    };
  };

  // A pre-session item's descriptor — the "Review & file" / "Mark as reported"
  // two-state control lives in PreSessionAction; only tone + front label + the
  // "past due" detail line differ by urgency. No "filed elsewhere" dismiss (the
  // whole-window report has no single-gift attestation to short-circuit).
  const preSessionDescriptor = (o: FilingObligationDto, overdue: boolean): QueueDescriptor => ({
    id: o.id,
    isRapid: false,
    tone: overdue ? 'rose' : 'amber',
    frontLabel: overdue ? 'File now' : 'Due soon',
    title: o.name,
    detail: (
      <>
        Due {formatDate(o.dueDate)}
        {o.daysRemaining < 0 ? ` · ${Math.abs(o.daysRemaining)} days past due` : ''}
      </>
    ),
    renderAction: (resolve, inert) => (
      <PreSessionAction obligation={o} onResolved={resolve} inert={inert} />
    ),
  });

  // A 48-hour-notice item's descriptor — the control (one- or two-state, by `canGenerate`) lives in
  // NoticeAction; only tone + front label + the detail line differ by urgency. No "filed elsewhere"
  // dismiss (the notice carries no schedule period to attest against).
  //
  // The detail line is where this differs from every other card: a 48-hour clock rounded to whole
  // days reads the same at 40 hours and at 3, so a notice still running shows the REAL time left
  // (`timeRemainingLabel`, hours or minutes). An overdue one says how late it is instead — how long
  // ago a missed statutory deadline passed is the number that matters then.
  const noticeDescriptor = (o: FilingObligationDto, overdue: boolean): QueueDescriptor => {
    const remaining = overdue ? null : timeRemainingLabel(o.deadlineUtc);
    return {
      id: o.id,
      isRapid: false,
      tone: overdue ? 'rose' : 'amber',
      frontLabel: overdue ? 'File now' : 'Due soon',
      title: o.name,
      detail: (
        <>
          Due {formatDate(o.dueDate)}
          {remaining ? ` · ${remaining}` : ''}
          {o.daysRemaining < 0 ? ` · ${Math.abs(o.daysRemaining)} days past due` : ''}
          {/* The gift itself, so the treasurer can reconcile the notice against their ledger
              without opening the confirm dialog — a notice is per-contribution. */}
          {o.contributionAmount != null && (
            <span className="mt-1 block text-slate-500">
              {formatAmount(o.contributionAmount)}
              {o.donorName ? ` from ${o.donorName}` : ''}
              {o.contributionDate ? ` · received ${formatDate(o.contributionDate)}` : ''}
            </span>
          )}
          <span className="mt-1 block text-slate-500" data-testid={`action-queue-notice-explainer-${o.id}`}>
            A gift this large received this close to an election must be reported on its own within
            48 hours of receiving it. It still goes on your regular report too.{' '}
            {noticeFilingModel(o, portal).filingTransport === 'email'
              ? `Email it to ${receivingOfficePhrase(portal.portalName)}.`
              : `File it in ${filingDestinationPhrase(noticeFilingModel(o, portal))}.`}
          </span>
        </>
      ),
      renderAction: (resolve, inert) => (
        <NoticeAction obligation={o} onResolved={resolve} inert={inert} />
      ),
    };
  };

  const descriptors: QueueDescriptor[] = [];

  // Rank: overdue rapids → overdue pre-session → owed daily → non-overdue rapids
  // → non-overdue pre-session. Pre-session ranks in the same tier as a rapid of
  // matching urgency (an imminent Jan-15 deadline rises).
  for (const o of overdueRapid) descriptors.push(rapidDescriptor(o, true));
  for (const o of overduePreSession) descriptors.push(preSessionDescriptor(o, true));
  for (const o of overdueNotice) descriptors.push(noticeDescriptor(o, true));

  for (const o of daily) {
    const perDayBody = alDailyObligationStripBody(
      formatAmount(o.dayGrossAmount ?? 0),
      formatDate(o.obligationDate ?? o.dueDate),
    );
    descriptors.push({
      id: o.id,
      isRapid: false,
      tone: 'rose',
      frontLabel: 'File now',
      title: 'Daily report due in the portal',
      // The per-day obligation body plus the standing AL daily-window explainer
      // sentence (the old standalone blue strip, now folded in here).
      detail: (
        <>
          {perDayBody}
          {windowExplainer && <span className="mt-1 block text-slate-500">{windowExplainer}</span>}
        </>
      ),
      renderAction: (resolve, inert) => (
        <DailyAction obligation={o} onResolved={resolve} inert={inert} />
      ),
    });
  }

  for (const o of upcomingRapid) descriptors.push(rapidDescriptor(o, false));
  for (const o of upcomingPreSession) descriptors.push(preSessionDescriptor(o, false));
  for (const o of upcomingNotice) descriptors.push(noticeDescriptor(o, false));

  return { descriptors, forceRapid, unready };
}
